import { JobPhrase } from "../../api/phrasebook";

export enum BulletPointType {
  GARMENT_TYPE = "GARMENT_TYPE",
  SPECIFIC_GARMENT_TYPE = "SPECIFIC_GARMENT_TYPE",
  GENDER = "GENDER",
  COMPOSITION = "COMPOSITION",
  FABRIC = "FABRIC",
  WEAVE = "WEAVE",
  COLOR = "COLOR",
  SIZES = "SIZES",
  FIT = "FIT",
  WEIGHT = "WEIGHT",
  MONOGRAM = "MONOGRAM",
  PROPER_NOUN = "PROPER_NOUN",
  SEASON = "SEASON",
  COUNTRY_OF_ORIGIN = "COUNTRY_OF_ORIGIN",
  CONTEXT_OF_USE = "CONTEXT_OF_USE",
  DETAIL = "DETAIL",
  CUSTOM = "CUSTOM",
}

export const BulletPointColors: { [key in BulletPointType]: string } = {
  GARMENT_TYPE: "rgba(84, 198, 235, 0.5)", // OK
  SPECIFIC_GARMENT_TYPE: "rgba(160, 204, 218, 0.5)",
  GENDER: "rgba(227, 119, 118, 0.6)", // OK
  SEASON: "rgba(255, 20, 147, 0.2)", // OK
  COLOR: "rgba(255, 69, 0, 0.3)", // NO
  COUNTRY_OF_ORIGIN: "rgba(209, 227, 221, 1)", // OK
  COMPOSITION: "rgba(167, 196, 194, 0.5)", // OK
  WEAVE: "rgba(219, 173, 106, 0.5)", // OK
  FABRIC: "rgba(219, 207, 176, 1)", // OK
  MONOGRAM: "rgba(255, 140, 0, 0.3)", // OK
  PROPER_NOUN: "rgba(71, 108, 155, 0.5)",
  WEIGHT: "rgba(198, 210, 237, 1)", // OK
  SIZES: "rgba(224, 177, 203, 0.7)", // OK
  DETAIL: "rgba(247, 204, 141, 1)", // OK
  FIT: "rgba(231, 227, 147, 1)", // OK
  CONTEXT_OF_USE: "rgba(255, 166, 158, 0.5)", // OK
  CUSTOM: "rgba(180, 180, 180, 0.5)", // Gray for custom
};

// Template variables available for Lara translation instructions
// These correspond to BulletPointType values and can be used with {variable_name} syntax
export const LARA_TEMPLATE_VARIABLES = [
  { name: "garment_type", label: "Garment Type", description: "Garment type" },
  { name: "gender", label: "Gender", description: "Gender" },
  { name: "color", label: "Color", description: "Product color" },
  {
    name: "specific_garment_type",
    label: "Specific Garment Type",
    description: "Specific garment type",
  },
  {
    name: "country_of_origin",
    label: "Country of Origin",
    description: "Country of origin",
  },
  {
    name: "composition",
    label: "Composition",
    description: "Material composition",
  },
  { name: "fabric", label: "Fabric", description: "Fabric type" },
  { name: "weave", label: "Weave", description: "Weave pattern" },
  { name: "monogram", label: "Monogram", description: "Monogram details" },
  {
    name: "proper_noun",
    label: "Proper Noun",
    description: "Brand/proper nouns",
  },
  { name: "weight", label: "Weight", description: "Product weight" },
  { name: "sizes", label: "Sizes", description: "Available sizes" },
  { name: "detail", label: "Detail", description: "Product details" },
  { name: "fit", label: "Fit", description: "Fit style" },
  {
    name: "context_of_use",
    label: "Context of Use",
    description: "Usage context",
  },
  { name: "season", label: "Season", description: "Season" },
] as const;

export type LaraTemplateVariable = (typeof LARA_TEMPLATE_VARIABLES)[number];

export enum JobStatus {
  NOT_READY = "NOT_READY",
  TO_BE_STARTED = "TO_BE_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  TO_BE_REVIEWED = "TO_BE_REVIEWED",
  TO_BE_REWORKED = "TO_BE_REWORKED",
  DELIVERED = "DELIVERED",
  COMPLETED = "COMPLETED",
}

export enum ClientJobStatus {
  NOT_READY = "NOT_READY",
  TO_BE_STARTED = "TO_BE_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  TO_BE_REVIEWED = "TO_BE_REVIEWED",
  COMPLETED = "COMPLETED",
}

export type Role =
  | "USER"
  | "ADMIN"
  | "MANAGER"
  | "COPYWRITER"
  | "CLIENT"
  | "COPYWRITER_CLIENT";

export type CopywriterFunction = "Editor" | "Reviewer";

export type DataSourceMode = "gdrive" | "manual";

export type User = {
  id?: string;
  email: string;
  name: string | null;
  surname: string | null;
  role: Role;
  password?: string;
  email_confirmed?: boolean;
  disabled?: boolean;
  organization_id?: string | null;

  // Terms and Conditions
  terms_accepted_version?: string | null;
  needs_terms_acceptance?: boolean;

  // modes
  mode_allow_create?: boolean;
  mode_allow_translate?: boolean;
  mode_allow_ai_generation?: boolean;
  mode_allow_translate_ai?: boolean;

  isAdmin?: boolean;
  isManager?: boolean;
  isCopywriter?: boolean;
  isClient?: boolean;
  isCopywriterClient?: boolean;
  isUser?: boolean;

  isClientRelated?: boolean;
};

export const statusToLabel = (
  status: JobStatus | ClientJobStatus,
  clientVariant: boolean = false
) => {
  const label = status.replace(/_/g, " ").toLowerCase().replace("uat", "UAT");

  if (clientVariant && status === JobStatus.COMPLETED) {
    return "Approved";
  }

  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const colorByStatus = {
  [JobStatus.NOT_READY]: "#000", // Gray - not yet ready to start
  [JobStatus.TO_BE_STARTED]: "#F57C00", // Orange - waiting to be started
  [JobStatus.IN_PROGRESS]: "#1E88E5", // Medium blue - work in progress
  [JobStatus.TO_BE_REVIEWED]: "#8E24AA", // Deep purple - internal review
  [JobStatus.TO_BE_REWORKED]: "#D81B60", // Pink/Magenta - requires changes
  [JobStatus.DELIVERED]: "#6D4C41", // Brown - client review/testing
  [JobStatus.COMPLETED]: "#43A047", // Medium green - successfully completed
};

// ── Content Groups (V2 settings) ──────────────────────────────────────

export type ContentGroupKind = "title" | "description" | "bullet_points";

export interface ContentGroupFormatting {
  paragraphWrapTag: string;
  phraseWrapTag: string;
  bulletPrefix: string;
  phraseSeparator: "space" | "newline" | "dash" | string;
  keyValueMode: boolean;
  keyWrapTag: string;
}

export const defaultContentGroupFormatting: ContentGroupFormatting = {
  paragraphWrapTag: "",
  phraseWrapTag: "",
  bulletPrefix: "",
  phraseSeparator: "space",
  keyValueMode: false,
  keyWrapTag: "",
};

export interface ContentGroupPhrases {
  minPhrases: number;
  maxPhrases: number;
  minCharacters: number;
  maxCharacters: number;
  minWords: number;
  maxWords: number;
}

export interface ContentGroup {
  id: string;
  kind: ContentGroupKind;
  label: string;
  enabled: boolean;
  sortOrder: number;
  phrases: ContentGroupPhrases;
  formatting: ContentGroupFormatting;
}

export const MAX_CONTENT_GROUPS = 31; // 1 title + 15 descriptions + 15 bullet points

// ── Export Rules ─────────────────────────────────────────────────────

export interface ExportRule {
  id: string;
  label: string;          // Column name in export (e.g. "In Evidenza")
  sourceGroups: string[];  // Content group IDs to merge
  // Separator inserted between merged source columns. When omitted, the
  // backend defaults to "\n\n" (blank line) for backwards compatibility.
  separator?: string;
}

// Default separator used when ExportRule.separator is undefined / empty.
export const DEFAULT_EXPORT_RULE_SEPARATOR = "\n\n";

export type BrandBatchSettingsWithPhrases = {
  enabled: boolean;
  minPhrases: number;
  maxPhrases: number;
  minCharacters: number;
  maxCharacters: number;
  minWords: number;
  maxWords: number;
};

export type BrandInstructions = {
  brand_context?: string;
  brand_rules?: string;
  brand_values?: string;
  brand_tone_of_voice?: string;
};

export interface CustomBulletPointMapping {
  column_name: string;
  custom_name: string;
  description?: string;
  mapped_to_type?: BulletPointType | "CUSTOM";
}

export type BrandBatchSettings = {
  title_instructions?: string;
  fabric_instructions?: string;
  brand_instructions?: BrandInstructions;
  color_instructions?: string;
  country_of_origin_instructions?: string;
  details_instructions?: string;
  description_instructions?: string;
  phrasebook_brands?: string[];
  custom_bullet_points?: CustomBulletPointMapping[] | null;
  languages_to_translate?: LanguageCode[];
  lara_languages?: LanguageCode[];
  lara_glossaries?: string[];
  lara_memories?: string[];
  lara_instructions?: string[];
  lara_style?: "faithful" | "fluid" | "creative";
  title_templates?: string[];

  content_groups?: ContentGroup[];
  export_rules?: ExportRule[];
};

const defaultPhrases: ContentGroupPhrases = {
  minPhrases: 1,
  maxPhrases: 1,
  minCharacters: 0,
  maxCharacters: 0,
  minWords: 0,
  maxWords: 0,
};

export const defaultContentGroups: ContentGroup[] = [
  { id: "TITLE", kind: "title", label: "Title", enabled: true, sortOrder: 0, phrases: { ...defaultPhrases }, formatting: { ...defaultContentGroupFormatting } },
  { id: "EDITORIAL_DESCRIPTION", kind: "description", label: "Regular Description", enabled: true, sortOrder: 1, phrases: { ...defaultPhrases }, formatting: { ...defaultContentGroupFormatting } },
  { id: "BULLET_POINTS", kind: "bullet_points", label: "Bullet Points", enabled: true, sortOrder: 2, phrases: { ...defaultPhrases }, formatting: { ...defaultContentGroupFormatting, phraseSeparator: "newline" } },
];

export const defaultSettings: BrandBatchSettings = {
  title_instructions: "",
  fabric_instructions: "",
  brand_instructions: {
    brand_context: "",
    brand_rules: "",
    brand_values: "",
    brand_tone_of_voice: "",
  },
  color_instructions: "",
  country_of_origin_instructions: "",
  details_instructions: "",
  description_instructions: "",
  phrasebook_brands: [],
  custom_bullet_points: null,
  languages_to_translate: [],
  lara_languages: [],
  lara_glossaries: [],
  lara_memories: [],
  lara_instructions: [],
  lara_style: "faithful",
  title_templates: [],
  content_groups: defaultContentGroups,
};

export type CommonFormField = {
  type: "text" | "select";
  key: string;
  label: string;
  value: string | undefined;
  required?: boolean;
  disabled?: boolean;
  options?: { value: string; label: string }[];
  helperAction?: {
    text: string;
    onClick: () => void;
  };
  showOnlyAction?: boolean; // If true, show only the action button without the field
};

export type BrandBatchCommonFields = {
  id?: string;
  created_at?: string;
  name: string;
  gdrive_folder_id?: string;
  original_language: LanguageCode;
  archetype_score_angelo_custode: number;
  archetype_score_amante: number;
  archetype_score_burlone: number;
  archetype_score_sovrano: number;
  archetype_score_innocente: number;
  archetype_score_ribelle: number;
  archetype_score_uomo_comune: number;
  archetype_score_creatore: number;
  archetype_score_mago: number;
  archetype_score_saggio: number;
  archetype_score_eroe: number;
  archetype_score_esploratore: number;
  narrative_perspective: string;
  sentences_amount: string;
  words_to_avoid: string[];
  expressions?: string[];
  bullet_points_order?: BulletPointType[];
  monograms?: Monogram[];
  settings?: BrandBatchSettings;
};

export interface Brand extends BrandBatchCommonFields {
  logo_name?: string;
  organization_id?: string | null;
  organization_mode_allow_ai_generation?: boolean;
  organization_mode_allow_translate?: boolean;
  organization_mode_allow_translate_ai?: boolean;
}

export type CreateBrandData = Omit<Brand, "created_at">;

export type UpdateBrandData = Omit<Brand, "created_at"> & Pick<Brand, "id">;

export type CopywriterJobToGoCounts = {
  to_go_as_editor: number;
  to_go_as_reviewer: number;
  total_to_go: number;
  jobs_by_statuses: Record<JobStatus, number>;
};

export type ClientJobToGoCounts = {
  delivered: number;
  completed: number;
  to_be_reworked: number;
  total: number;
  jobs_by_statuses: Record<JobStatus, number>;
};

export type SheetDataItem = {
  [key: string]: string;
};

export type SheetData = {
  items: SheetDataItem[];
  columns?: string[];
  file_name?: string;
};

export type ColumnDescription = {
  id: string;
  description: string;
  hasChanges?: boolean;
};

export interface Batch extends BrandBatchCommonFields {
  collection_name?: string;
  brand_id: string;
  brand_name?: string;
  season?: string;
  copywriter_jobs_to_go?: CopywriterJobToGoCounts;
  client_jobs_to_go?: ClientJobToGoCounts;
  batch_configuration_completed?: boolean;
  stats?: BatchStats;
  advanced_stats?: BatchAdvancedStats;
  client_owned?: boolean;
  created_by_name?: string;
  created_by_surname?: string;
  pending_configuration?: boolean;
  organization_mode_allow_ai_generation?: boolean;
  organization_mode_allow_translate?: boolean;
  organization_mode_allow_translate_ai?: boolean;
}

export type BatchStats = {
  total_jobs: number;
  jobs_by_status: Record<JobStatus, number>;
  remaining_jobs: number;
  garment_types_count: Record<string, number>;
  jobs_with_missing_images_in_gdrive: number; // missing from drive
  jobs_without_images: number; // needs loading
  jobs_with_images: number;
  jobs_with_ai_processing: number;
  copywriters?: JobCopywriters;
};

/**
 * Advanced statistics for a batch including word counts, phrase reuse metrics, and content utilization patterns
 */
export type BatchAdvancedStats = {
  job_count: number;
  average_words_per_job: number;
  average_words_by_type: Record<string, number>; // Maps PhraseType to average word count
  translation_metrics: {
    languages: string[]; // List of language codes to translate to
    total_words: number; // Total words
    total_words_to_deliver: number; // Total words counting each job usage
    total_words_to_translate: number; // Total words that need translation
    word_reuse_percentage: number; // Percentage of words that don't need new translation
    by_language: Record<
      string,
      {
        // Key is the language code
        words_to_translate: number; // Total words that need this translation
      }
    >;
  };
};

export type CreateBatchData = Omit<Batch, "created_at">;

export type UpdateBatchData = Omit<Batch, "created_at"> &
  Pick<Batch, "id"> & {
    client_owned?: boolean;
  };

export interface BatchFolder {
  id: string;
  name: string;
  type: string;
}

export interface Monogram {
  id: string;
  imagePath: string;
  text: string;
}

export interface BulletPoint {
  id: string;
  variants: string[];
  type: BulletPointType;
  created_at: string;
  approved: boolean;
  job_id: string;
  custom_name?: string;
  custom_description?: string;
}

export interface CopywriterInfo {
  id: string;
  name: string;
  surname: string;
  email: string;
}

export interface JobCopywriters {
  editors: CopywriterInfo[];
  reviewers: CopywriterInfo[];
}

export interface Job {
  id: string;
  master_id: string;
  batch_id: string;
  user_feedback: string | null;
  client_notes: string | null;
  status: JobStatus;
  bullet_points_order?: string[];
  created_at: string;
  images: string[] | null;
  thumbnails: string[] | null;
  missing_images: boolean;
  ai_processing: boolean;
  ai_processed: boolean;
  ai_skip: boolean;
  garment_type: string | null;
  phrases: JobPhrase[];
  is_editor?: boolean;
  is_reviewer?: boolean;
  copywriters?: JobCopywriters;
  title?: string;
  short_description_phrases?: string[];
  editorial_description_phrases?: string[];
  long_description_phrases?: string[];
  bullet_points_phrases?: string[];
  bullet_points_1_phrases?: string[];
  bullet_points_2_phrases?: string[];
  has_ai_translations?: boolean;
}

export type TranslationType = "ai" | "human";

export interface JobTranslations {
  translations: Record<LanguageCode, Record<string, string[]>>;
  originals: Record<string, string[]>;
  source_language: LanguageCode | null;
  translation_type: TranslationType;
}

export enum LanguageCode {
  EN_UK = "en_uk",
  EN_US = "en_us",
  IT = "it",
  FR = "fr",
  DE = "de",
  ES = "es",
  RU = "ru",
  PL = "pl",
  NL = "nl",
  TR = "tr",
  EL = "el",
  PT = "pt",
  AR = "ar",
  JA = "ja",
  ZH = "zh",
  KO = "ko",
  DA = "da",
  CS = "cs",
  HU = "hu",
}

export const languageCodeToLabel = (languageCode: LanguageCode) => {
  switch (languageCode) {
    case LanguageCode.EN_UK:
      return "English (UK)";
    case LanguageCode.EN_US:
      return "English (US)";
    case LanguageCode.IT:
      return "Italian";
    case LanguageCode.FR:
      return "French";
    case LanguageCode.DE:
      return "German";
    case LanguageCode.ES:
      return "Spanish";
    case LanguageCode.RU:
      return "Russian";
    case LanguageCode.PL:
      return "Polish";
    case LanguageCode.NL:
      return "Dutch";
    case LanguageCode.TR:
      return "Turkish";
    case LanguageCode.EL:
      return "Greek";
    case LanguageCode.PT:
      return "Portuguese";
    case LanguageCode.AR:
      return "Arabic";
    case LanguageCode.JA:
      return "Japanese";
    case LanguageCode.ZH:
      return "Chinese";
    case LanguageCode.KO:
      return "Korean";
    case LanguageCode.DA:
      return "Danish";
    case LanguageCode.CS:
      return "Czech";
    case LanguageCode.HU:
      return "Hungarian";
  }
};
